import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import {
  Eye,
  EyeOff,
  ShieldCheck,
  Loader2,
  Mail,
  Lock,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { auth, db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError("");
    setNotice("");
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.email || !form.password) {
      setError("Please enter email and password.");
      return;
    }
    try {
      setLoading(true);
      const response = await signInWithEmailAndPassword(auth, form.email, form.password);
      const user = response.user;
      const adminDoc = await getDoc(doc(db, "admins", user.uid));
      if (!adminDoc.exists()) {
        await signOut(auth);
        setError("You are not authorized as admin.");
        return;
      }
      navigate("/admin/dashboard");
    } catch (err) {
      console.log(err);
      if (err.code === "auth/user-not-found") setError("Admin account not found.");
      else if (err.code === "auth/wrong-password") setError("Incorrect password.");
      else if (err.code === "auth/invalid-email") setError("Invalid email address.");
      else setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!form.email) { setError("Enter email first."); return; }
    try {
      await sendPasswordResetEmail(auth, form.email);
      setNotice("Password reset email sent.");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="grid min-h-screen lg:grid-cols-2">
        {/* LEFT BRAND */}
        <div className="relative hidden flex-col justify-between overflow-hidden bg-brand-gradient p-12 text-white lg:flex">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-white/10" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-white/5" />

          <div className="relative">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 backdrop-blur">
                <ShieldCheck size={28} />
              </div>
              <div>
                <h1 className="font-display text-2xl font-bold">Karanki Matrimony</h1>
                <p className="text-[12px] font-semibold tracking-wider text-white/75">ADMIN PANEL</p>
              </div>
            </div>

            <div className="mt-14">
              <h2 className="font-display text-4xl font-bold leading-tight xl:text-5xl">
                Manage your matrimony platform
              </h2>
              <p className="mt-5 max-w-md text-[15px] leading-7 text-white/80">
                Profiles, verifications, premium memberships, payments and reports — all in one elegant control center.
              </p>
            </div>
          </div>

          <div className="relative grid grid-cols-3 gap-3">
            {[
              { v: "1200+", l: "Users" },
              { v: "320", l: "Premium" },
              { v: "98%", l: "Trust" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur">
                <p className="font-display text-2xl font-bold">{s.v}</p>
                <p className="mt-1 text-[11px] tracking-wide text-white/75">{s.l}</p>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT FORM */}
        <div className="flex items-center justify-center px-4 py-10 sm:px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md"
          >
            <div className="mb-8 text-center lg:hidden">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-brand-gradient text-white shadow-glow">
                <ShieldCheck size={28} />
              </div>
              <h1 className="mt-4 font-display text-2xl font-bold text-ink-800">Karanki Admin</h1>
            </div>

            <div className="card-elevated p-6 sm:p-8">
              <span className="chip">
                <Sparkles size={12} /> Secure access
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-ink-800">
                Welcome back
              </h2>
              <p className="mt-1.5 text-[14px] text-ink-500">Sign in to your admin dashboard</p>

              <form onSubmit={handleLogin} className="mt-7 space-y-5">
                <div>
                  <label className="field-label">Email Address</label>
                  <div className="input-group">
                    <Mail size={17} className="text-ink-400" />
                    <input
                      type="email"
                      placeholder="admin@karanki.com"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div>
                  <label className="field-label">Password</label>
                  <div className="input-group">
                    <Lock size={17} className="text-ink-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={form.password}
                      onChange={(e) => updateField("password", e.target.value)}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="text-ink-400 hover:text-brand-600"
                    >
                      {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-[13px] font-semibold text-brand-600 hover:underline"
                  >
                    Forgot password?
                  </button>
                </div>

                {error && (
                  <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
                    {error}
                  </div>
                )}
                {notice && (
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-700">
                    {notice}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full py-4 text-[15px]"
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  Login to dashboard
                  {!loading && <ChevronRight size={16} />}
                </button>
              </form>
            </div>

            <p className="mt-6 text-center text-[12px] text-ink-300">
              Karanki Matrimony Admin Panel
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
